import { useCallback, useEffect, useMemo, useState } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Calculator } from "@phosphor-icons/react";

const fmt = (v) => `£${(Number(v) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Shared SDLT calculator — the exact same HMRC rate engine used on the
 * Conveyancing Fees card, surfaced wherever a solicitor needs it (e.g.
 * inside the SDLT return form) so they don't have to jump between screens.
 *
 * onApplied(total, params, calc) — fires when the user clicks Apply.
 */
export default function SdltCalculatorDialog({ open, onClose, caseId, seedValue = 0, sdltParams, onApplied }) {
  const [params, setParams] = useState({
    property_value: 0,
    first_time_buyer: false,
    second_home: false,
    ltd_company: false,
    commercial: false,
    non_uk_resident: false,
    lease_npv: 0,
    linked_consideration: 0,
    corporate_body: false,
  });
  const [rentAnnual, setRentAnnual] = useState(0);
  const [rentYears, setRentYears] = useState(5);
  const [calc, setCalc] = useState(null);
  const [calcing, setCalcing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setParams({
      property_value: Number(sdltParams?.property_value || seedValue || 0) || 0,
      first_time_buyer: !!sdltParams?.first_time_buyer,
      second_home: !!sdltParams?.second_home,
      ltd_company: !!sdltParams?.ltd_company,
      commercial: !!sdltParams?.commercial,
      non_uk_resident: !!sdltParams?.non_uk_resident,
      lease_npv: sdltParams?.lease_npv || 0,
      linked_consideration: sdltParams?.linked_consideration || 0,
      corporate_body: !!sdltParams?.corporate_body,
    });
    setRentAnnual(0);
    setRentYears(5);
    setCalc(null);
  }, [open, sdltParams, seedValue]);

  const computedNpv = useMemo(() => {
    const r = Number(rentAnnual) || 0;
    const n = Math.max(1, Number(rentYears) || 0);
    if (r <= 0) return 0;
    const factor = (1 - Math.pow(1.035, -n)) / 0.035;
    return Math.round(r * factor * 100) / 100;
  }, [rentAnnual, rentYears]);

  const useComputedNpv = () => setParams((p) => ({ ...p, lease_npv: computedNpv }));

  const runCalc = useCallback(async () => {
    setCalcing(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/fees/sdlt/calculate`, params);
      setCalc(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Calculation failed");
    } finally {
      setCalcing(false);
    }
  }, [caseId, params]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="sdlt-calc-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Stamp Duty Land Tax calculator</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <Label className="text-xs">Chargeable consideration / lease premium (£)</Label>
            <Input
              type="number"
              value={params.property_value}
              onChange={(e) => setParams((p) => ({ ...p, property_value: Number(e.target.value) || 0 }))}
              data-testid="sdlt-calc-value-input"
            />
          </div>
          {[
            ["first_time_buyer", "First-time buyer"],
            ["second_home", "Additional property / second home (+5%)"],
            ["ltd_company", "Limited company purchase"],
            ["non_uk_resident", "Non-UK resident (+2% surcharge)"],
            ["corporate_body", "Corporate body — flat 17% on residential >£500k (Sched 4A)"],
            ["commercial", "Commercial / mixed-use (uses non-residential SDLT rates)"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={params[key]}
                onCheckedChange={(val) => setParams((p) => {
                  if (key === "commercial" && val) {
                    return { ...p, commercial: true, first_time_buyer: false, second_home: false, ltd_company: false, non_uk_resident: false, corporate_body: false };
                  }
                  if (key === "corporate_body" && val) {
                    return { ...p, corporate_body: true, first_time_buyer: false, commercial: false, non_uk_resident: false };
                  }
                  return { ...p, [key]: !!val };
                })}
                data-testid={`sdlt-calc-${key.replace(/_/g, "-")}`}
                disabled={
                  (key !== "commercial" && params.commercial) ||
                  (key === "first_time_buyer" && params.corporate_body) ||
                  (key === "non_uk_resident" && params.corporate_body)
                }
              />
              <span className={`text-xs ${
                ((key !== "commercial" && params.commercial) ||
                  (key === "first_time_buyer" && params.corporate_body) ||
                  (key === "non_uk_resident" && params.corporate_body))
                  ? "text-slate-400" : ""
              }`}>{label}</span>
            </label>
          ))}

          <div className="border border-slate-200 bg-amber-50/40 rounded-lg p-2.5 space-y-2">
            <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">New Lease — Tax on Rent (NPV)</p>
            <p className="text-[11px] text-slate-500 leading-snug">
              SDLT on a new lease applies to the premium <em>and</em> separately to the Net Present Value of rent. Helper computes NPV at HMRC's 3.5% discount rate.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-slate-500">Annual rent (£)</Label>
                <Input type="number" value={rentAnnual} onChange={(e) => setRentAnnual(Number(e.target.value) || 0)} className="h-8 text-xs" data-testid="sdlt-calc-rent-annual" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-slate-500">Years</Label>
                <Input type="number" value={rentYears} onChange={(e) => setRentYears(Number(e.target.value) || 0)} className="h-8 text-xs" data-testid="sdlt-calc-rent-years" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-600">
                Computed NPV: <span className="font-semibold tabular-nums">{fmt(computedNpv)}</span>
              </div>
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={useComputedNpv} data-testid="sdlt-calc-npv-use-btn" disabled={!computedNpv}>
                Use as NPV
              </Button>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-500">Lease NPV (£) — applied to SDLT</Label>
              <Input
                type="number"
                value={params.lease_npv}
                onChange={(e) => setParams((p) => ({ ...p, lease_npv: Number(e.target.value) || 0 }))}
                className="h-8 text-xs"
                data-testid="sdlt-calc-lease-npv"
              />
            </div>
          </div>

          <div className="border border-slate-200 bg-violet-50/40 rounded-lg p-2.5 space-y-1.5">
            <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Linked Transactions</p>
            <p className="text-[11px] text-slate-500 leading-snug">
              If part of a single arrangement with other property purchases between the same parties, enter the combined consideration of the OTHER linked properties.
            </p>
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-500">Other linked consideration (£)</Label>
              <Input
                type="number"
                value={params.linked_consideration}
                onChange={(e) => setParams((p) => ({ ...p, linked_consideration: Number(e.target.value) || 0 }))}
                className="h-8 text-xs"
                data-testid="sdlt-calc-linked-consideration"
              />
            </div>
          </div>

          <Button onClick={runCalc} disabled={calcing} variant="outline" size="sm" className="w-full h-8 text-xs gap-1.5" data-testid="sdlt-calc-run-btn">
            <Calculator weight="bold" className="w-3.5 h-3.5" /> {calcing ? "Calculating…" : "Calculate"}
          </Button>
          {calc && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1 text-xs" data-testid="sdlt-calc-result">
              <div className="flex justify-between font-semibold text-slate-900">
                <span className="flex items-center gap-1 flex-wrap">
                  Total SDLT
                  {calc.schedule === "non_residential" && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700">Non-residential</span>}
                  {calc.non_uk_resident && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700">+2% Non-UK</span>}
                  {calc.linked_active && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">Linked</span>}
                  {calc.npv_total > 0 && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Lease NPV</span>}
                  {calc.corporate_flat_active && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800">Corporate 17%</span>}
                </span>
                <span>{fmt(calc.total)}</span>
              </div>
              {(calc.npv_total > 0 || calc.linked_active) && (
                <div className="border-t border-slate-200 mt-1 pt-1 space-y-0.5 text-[11px]">
                  <div className="flex justify-between text-slate-600">
                    <span>Premium SDLT{calc.linked_active ? " (prorated)" : ""}</span>
                    <span className="tabular-nums">{fmt(calc.premium_total)}</span>
                  </div>
                  {calc.npv_total > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>Rent NPV SDLT</span>
                      <span className="tabular-nums">{fmt(calc.npv_total)}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="border-t border-slate-200 mt-1 pt-1 space-y-0.5 text-[11px] text-slate-500">
                {(calc.bands || []).map((b, i) => (
                  <div key={`pre-${i}`} className="flex justify-between">
                    <span>Premium band {i + 1} @ {b.rate}%</span>
                    <span>{fmt(b.tax || 0)}</span>
                  </div>
                ))}
                {(calc.npv_bands || []).map((b, i) => (
                  <div key={`npv-${i}`} className="flex justify-between">
                    <span>NPV band {i + 1} @ {b.rate}%</span>
                    <span>{fmt(b.tax || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!calc}
            onClick={() => onApplied(calc.total || 0, params, calc)}
            className="text-white"
            style={{ background: "#100f5d" }}
            data-testid="sdlt-calc-apply-btn"
          >
            Apply £{(calc?.total || 0).toLocaleString("en-GB")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { fmt as formatGbp };
